import styled from "styled-components";
import {useCallback, useEffect, useRef, useState} from "react";
import {IconButton} from "../../ui/widgets/button/IconButton";
import {SelectInput} from "../../ui/widgets/input/select/SelectInput";
import {Field} from "../../ui/widgets/form/field/Field";
import {ClickToEditInput} from "../../ui/widgets/input/clicktoedit/ClickToEditInput";
import {Button} from "../../ui/widgets/button/Button";
import {fontSize, fontWeight} from "../../ui/5.0/styles/styles";
import {colorPalette} from "../../ui/5.0/styles/colorPalette";

interface IWebCamProps {
	readonly onClose: () => void;
	readonly onComplete: (data: {base64: string; fileName: string; extension: string}) => void;
}

interface ICameraOption {
	label: string;
	deviceId: string;
}

export const WebCam = (props: IWebCamProps) => {
	const [cameraOptions, setCameraOptions] = useState<ICameraOption[]>([]);
	const [selectedCamera, setSelectedCamera] = useState<ICameraOption | null>(null);

	const timerMax = 60;
	const [timerValue, setTimerValue] = useState<number>(0);
	const [timer, setTimer] = useState<number>(0);

	const [capturedImage, setCapturedImage] = useState<string | null>(null);
	const [capturedImageName, setCapturedImageName] = useState<string | null>(null);
	const [capturedImageExtension, setCapturedImageExtension] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);

	const videoRef = useRef<HTMLVideoElement | null>(null);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const streamRef = useRef<MediaStream | null>(null);

	const getCameraDevices = async () => {
		const devices = await navigator.mediaDevices.enumerateDevices();

		return devices
			.filter((device) => device.kind === "videoinput")
			.map((device) => ({
				label: device.label || "Camera",
				deviceId: device.deviceId,
			}));
	};

	const handleTimerInputChange = (newValue: string) => {
		const valueAsNumber = parseInt(newValue, 10);
		const safeValue = isNaN(valueAsNumber) ? 0 : valueAsNumber;

		setTimerValue(safeValue);
	};

	const handleCapturedImageNameChange = (newValue: string) => {
		setCapturedImageName(newValue);
	};

	const getErrorMessage = (value: string) => {
		const timerValue = parseInt(value, 10);
		if (timerValue < 0) {
			return "Time should be greater than 0";
		} else if (timerValue > timerMax) {
			return "Maximum time is 60s";
		}
	};

	const getPhotoNameError = (value: string) => {
		return value.trim() === "" ? "Photo name cannot be empty" : "";
	};

	const onCancelClick = () => {
		if (timer > 0 || capturedImage) {
			handleRetake();
		} else {
			if (!capturedImage) {
				props.onClose();
			}
		}
	};

	const handleCapture = () => {
		if (timerValue > 1) {
			//If user clicked on Capture after entering the timer value
			setTimer(timerValue);
		} else {
			capturePhoto();
		}
	};

	const capturePhoto = () => {
		if (videoRef.current && canvasRef.current) {
			const video = videoRef.current;
			const canvas = canvasRef.current;
			canvas.width = video.videoWidth;
			canvas.height = video.videoHeight;
			const ctx = canvas.getContext("2d");

			if (ctx) {
				ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

				const extension: "webp" | "jpg" = "webp";
				const format: "webp" | "jpeg" = (extension as string) === "jpg" ? "jpeg" : "webp";

				const fileName = new Date().toLocaleString().replaceAll(", ", "_").replaceAll(" ", "_");
				const image = canvas.toDataURL(`image/${format}`, 0.78);

				setCapturedImage(image);
				setCapturedImageName(fileName);
				setCapturedImageExtension(format);
			}
		}
	};

	const handleRetake = async () => {
		setCapturedImage(null);
		setCapturedImageName(null);
		setCapturedImageExtension(null);
		setTimerValue(0);
		setTimer(0);

		// Restart the stream (this ensures video element gets a valid stream again)
		await startStream();
	};

	const handleConfirmUpload = (capturedImageSrc: string, fileName: string, extension: string) => {
		if (!capturedImageSrc) {
			return;
		}

		props.onComplete({
			base64: capturedImageSrc,
			fileName,
			extension,
		});
	};

	const startStream = useCallback(async () => {
		if (!selectedCamera) {
			return;
		}

		try {
			// Stop previous stream
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop());
			}

			const stream = await navigator.mediaDevices.getUserMedia({
				video: {deviceId: selectedCamera.deviceId},
				audio: false,
			});

			if (videoRef.current) {
				videoRef.current.srcObject = stream;
				await videoRef.current.play();

				const visualWidth = 480;
				const visualHeight = 360;

				videoRef.current.style.width = `${visualWidth}px`;
				videoRef.current.style.height = `${visualHeight}px`;
			}

			streamRef.current = stream;
		} catch (err) {
			console.error("Error accessing camera:", err);
		}
	}, [selectedCamera]);

	useEffect(() => {
		const fetchCameras = async () => {
			try {
				setLoading(true);

				const tempStream = await navigator.mediaDevices.getUserMedia({video: true, audio: false});

				const devices = await getCameraDevices();
				setCameraOptions(devices);

				if (devices.length > 0) {
					setSelectedCamera(devices[0]); // default select first
				}

				tempStream.getTracks().forEach((track) => track.stop());
			} catch (err) {
				console.error("Camera access denied or error occurred:", err);
			} finally {
				setLoading(false); // stop loading
			}
		};

		fetchCameras();
	}, []);

	useEffect(() => {
		if (timer === 0) {
			return;
		} // If the timer is 0, don't start the interval.

		const interval = setInterval(() => {
			setTimer((prev) => {
				if (prev > 1) {
					return prev - 1;
				} else {
					clearInterval(interval);
					capturePhoto();
					return 0;
				}
			});
		}, 1000);

		// Cleanup the interval when the component is unmounted or the timer changes
		return () => clearInterval(interval);
	}, [timer]);

	useEffect(() => {
		startStream();

		return () => {
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop());
			}
		};
	}, [startStream]);

	const popupTitle = capturedImage ? "Upload Image" : "Take Photo";

	return (
		<WebCamStyled>
			<div className="popupBackdrop flexCenter">
				<div className="PopupWindow">
					<div className="title hbox alignCenter">
						{popupTitle}
						<IconButton
							className="closeBtn"
							icon="close"
							onClick={props.onClose}
						/>
					</div>

					<div className="webCamContainer">
						{!capturedImage && (
							<SelectInput
								selected={selectedCamera}
								onChange={setSelectedCamera}
								options={cameraOptions}
								optionsZIndex={999999}
								fullWidth={true}
								render={(device: ICameraOption) => device.label}
							/>
						)}

						{capturedImage ? (
							<img
								src={capturedImage}
								alt="Captured"
								style={{borderRadius: "8px"}}
							/>
						) : (
							<div className="VideoContainer">
								{timer > 0 && <div className="TimerOverlay">{timer}</div>}
								{loading && (
									<div className="LoadingOverlay">
										<p>Loading...</p>
									</div>
								)}
								<video
									ref={videoRef}
									autoPlay
									playsInline
									muted
								/>
							</div>
						)}
						<canvas
							ref={canvasRef}
							style={{display: "none"}}
						/>

						{!capturedImage ? (
							<Field
								className="TypeSelector"
								label="Timer (seconds)"
							>
								<ClickToEditInput
									value={timerValue}
									placeholder={"0"}
									inputType="number"
									onChange={handleTimerInputChange}
									getErrorMessage={getErrorMessage}
									noButtons={true}
									disabled={timer > 0}
								/>
							</Field>
						) : (
							<Field
								className="PhotoName"
								label="Photo Name "
							>
								<ClickToEditInput
									value={capturedImageName}
									className="capturedImage"
									onChange={handleCapturedImageNameChange}
									getErrorMessage={getPhotoNameError}
								/>
							</Field>
						)}

						<div className="buttonContainer">
							<Button
								className="Button"
								title="Cancel"
								label="Cancel"
								onClick={onCancelClick}
							/>
							<Button
								className="primary Button"
								title="Confirm"
								label="Confirm"
								onClick={capturedImage ? () => handleConfirmUpload(capturedImage, capturedImageName, capturedImageExtension) : handleCapture}
								disabled={timer > 0}
							/>
						</div>
					</div>
				</div>
			</div>
		</WebCamStyled>
	);
};

const WebCamStyled = styled.div`
	.PopupWindow {
		width: 500px;
	}

	.webCamContainer {
		padding: 10px;
	}

	.SelectInput {
		width: 100%;
		margin-bottom: 10px;
	}

	video,
	img {
		width: 480px;
		height: 360px;
		background-color: black;
		object-fit: cover;
		margin-bottom: 10px;
	}

	.VideoContainer {
		position: relative;
		width: fit-content;
		margin-bottom: 10px;
	}

	.TimerOverlay,
	.LoadingOverlay {
		position: absolute;
		left: 50%;
		transform: translateX(-50%);
		color: ${colorPalette.white};
		opacity: 50%;
		padding: 4px 8px;
		border-radius: 6px;
		z-index: 10;
		transition: all 0.3s ease;
	}

	.TimerOverlay {
		top: 35%;
		font-weight: ${fontWeight.bold};
		font-size: 128px;
	}

	.LoadingOverlay {
		top: 50%;
		font-weight: ${fontWeight.semiBold};
		font-size: ${fontSize.lg};
	}

	.Field {
		height: 45px;
	}

	.ClickToEditInput.noButtons .infoIcon {
		right: 0px;
		top: 13px;
	}

	.buttonContainer {
		display: flex;
		justify-content: flex-end;
		gap: 8px;
		margin: 16px 16px;

		.Button {
			margin: 0px;
		}
	}
`;
